import CustomImage from "@/components/Atoms/Image";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import { MultiPurposeTitle } from "@/components/Atoms/Title";
import { VariantProps, cva } from "class-variance-authority";
import styles from "./index.module.scss";
import { useState } from "react";
import { ButtonMaster } from "@/components/Atoms/Buttons";

export type BlockVariantProps = VariantProps<typeof BlockVariants>;
const BlockVariants = cva(
  ` grid container mx-auto gap-4 lg:gap-8 pb-6 px-6 md:px-0  2xl:max-w-[1360px]`,
  {
    variants: {
      gridDisplay: {
        half: `grid-cols-2`,
        third: `grid-cols-1 md:grid-cols-3 `,
        quarter: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
      },
    },
    defaultVariants: {
      gridDisplay: "third",
    },
  }
);

interface Props {
  __typename: string;
  composer: any;
  backgroundColor: any;
  gridDisplay: "half" | "third" | "quarter";
  textColor: any;
  titleColor: any;
  titleType: "default" | "highlight";
  addHeader: boolean;
  title: string;
  label: string;
  composerType: "default" | "people";
  loadButtonConfig: any;
}

export default function MultiPurposeBlock({
  composer,
  backgroundColor,
  textColor,
  gridDisplay,
  titleType,
  titleColor,
  title,
  label,
  addHeader,
  composerType,
  loadButtonConfig,
}: Props) {
  const [visibleItems, setVisibleItems] = useState(12);

  function loadMore() {
    setVisibleItems((prev) => {
      const newCount = prev + 12;

      return newCount;
    });
  }

  const itemsToShow =
    composerType === "people" ? composer?.slice(0, visibleItems) : composer;

  const hasMoreItems =
    composerType === "people" && composer?.length > visibleItems;

  return (
    <div
      style={{ backgroundColor: backgroundColor?.color.hex }}
      className={` py-6 md:py-12  `}
    >
      {addHeader && (
        <div className="px-6 lg:px-4 xl:px-2 2xl:px-0 container mx-auto text-center mb-12 gap-4 max-w-3xl">
          {label && (
            <MultiPurposeParagraph
              type="default"
              data={label}
              color={titleColor.color}
            />
          )}
          <MultiPurposeTitle
            type="default"
            tag="h3"
            data={title}
            color={titleColor?.color}
            columns={"single"}
          />
        </div>
      )}
      <div className={BlockVariants({ gridDisplay })}>
        {itemsToShow?.map((entry: any, index: number) => {
          switch (composerType) {
            case "people":
              return (
                <div
                  key={index}
                  className="flex flex-col gap-4 justify-start items-start text-left"
                >
                  {entry.image && (
                    <div className="  relative overflow-hidden w-full">
                      <CustomImage
                        data={entry.image}
                        className={`w-full h-full object-cover aspect-square ${styles.image}`}
                      />
                    </div>
                  )}
                  <div className="flex flex-col ">
                    {" "}
                    {entry.title && (
                      <MultiPurposeParagraph
                        type="headline"
                        data={entry.title}
                        color={textColor?.color}
                      />
                    )}
                    {entry.subtitle && (
                      <MultiPurposeParagraph
                        type="label"
                        data={entry.subtitle}
                        color={titleColor?.color}
                      />
                    )}
                  </div>

                  {entry.text && (
                    <div className="max-w-xs">
                      <MultiPurposeParagraph
                        type="default"
                        data={entry.text}
                        color={textColor?.color}
                      />
                    </div>
                  )}
                </div>
              );

            case "default":
            default:
              return (
                <div
                  key={index}
                  className="flex flex-col gap-6 justify-start items-center"
                >
                  <div className="flex flex-col">
                    {" "}
                    {entry.title && (
                      <MultiPurposeParagraph
                        type={`${
                          titleType == "default"
                            ? "headline"
                            : "headlineFeatured"
                        }`}
                        data={entry.title}
                        color={textColor?.color}
                      />
                    )}
                    {entry.image && (
                      <div className="w-12 h-12 lg:w-20 lg:h-20 relative">
                        <CustomImage data={entry.image} />
                      </div>
                    )}
                  </div>

                  <div className="max-w-[50%] md:max-w-full">
                    <MultiPurposeParagraph
                      type={`${
                        titleType == "default" ? "default" : "headline"
                      }`}
                      data={entry?.text}
                      color={textColor?.color}
                    />
                  </div>
                </div>
              );
          }
        })}
      </div>

      {hasMoreItems && (
        <div className="flex justify-center my-6 lg:mt-12">
          <ButtonMaster
            onClick={loadMore}
            anim={"slideLeft"}
            type={loadButtonConfig.buttonStyle}
            cta={loadButtonConfig.typeColor?.color}
            color={loadButtonConfig.labelColor?.color}
            customIcon={loadButtonConfig?.icon?.img}
            customColor={loadButtonConfig?.iconColor?.color}
            target=""
          >
            {loadButtonConfig?.buttonLabel?.label}
          </ButtonMaster>
        </div>
      )}
    </div>
  );
}

export const fragment = `
    fragment SectionMultiPurposeBlock on SectionMultiPurposeBlockRecord{
        __typename
        id
        backgroundColor {
          color {
            hex
          }
        }
        textColor {
          color {
            hex
          }
        }
        titleColor {
          color {
            hex
          }
        }
        loadButtonConfig {
          ...pageRelatedFragment
        }
        titleType
        gridDisplay
        addHeader
        title
        label
        composerType
        composer {
          ... on TextRecord {
            id
            addTitle
            title
            text
          }
          ... on TextWithImageRecord {
            id
            title
            addSubtitle
            subtitle
            text
            image {
              url
              focalPoint {
                y
                x
              }
              responsiveImage (imgixParams: {q: "65"}) {
                ...responsiveImageFragment
              }
            }
          }
        }
    }
`;
